import React, { useState, useEffect } from 'react';
import {
  Card, Input, Select, DatePicker, Button, Space, Tag, Dropdown, message,
  Popover, Form, Switch, Divider, Badge, Tooltip
} from 'antd';
import {
  SearchOutlined, FilterOutlined, SaveOutlined, DeleteOutlined,
  CloseCircleOutlined, StarOutlined, ClockCircleOutlined,
  FireOutlined, UserOutlined, TeamOutlined, GlobalOutlined,
  DownOutlined, CheckOutlined
} from '@ant-design/icons';
import styled from 'styled-components';
import dayjs from 'dayjs';

const { RangePicker } = DatePicker;

/**
 * AdvancedFilters - Comprehensive filtering system
 * Features:
 * - Quick filters (My Templates, Recent, Popular, Starred)
 * - Saved filter presets
 * - Full-text search across all fields
 * - Multi-criteria filters
 * - Filter persistence to localStorage
 */
const AdvancedFilters = ({ onFilterChange, initialFilters = {} }) => {
  const [form] = Form.useForm();
  const [activeFilters, setActiveFilters] = useState({});
  const [savedPresets, setSavedPresets] = useState([]);
  const [showSavePreset, setShowSavePreset] = useState(false);
  const [presetName, setPresetName] = useState('');
  const [quickFilter, setQuickFilter] = useState(null);

  // Load saved presets from localStorage
  useEffect(() => {
    const saved = localStorage.getItem('templateFilterPresets');
    if (saved) {
      try {
        setSavedPresets(JSON.parse(saved));
      } catch (error) {
        console.error('Failed to load saved presets:', error);
      }
    }
  }, []);

  // Apply initial filters
  useEffect(() => {
    if (Object.keys(initialFilters).length > 0) {
      form.setFieldsValue(initialFilters);
      setActiveFilters(initialFilters);
    }
  }, [initialFilters]);

  const handleQuickFilter = (filterType) => {
    let filters = {};

    switch (filterType) {
      case 'my-templates':
        // Filter to show templates created by current user
        filters = {
          scope: 'USER',
          sortBy: 'createdAt',
          sortOrder: 'desc',
        };
        break;

      case 'recent':
        // Show templates modified in last 7 days
        filters = {
          dateRange: [dayjs().subtract(7, 'days'), dayjs()],
          sortBy: 'updatedAt',
          sortOrder: 'desc',
        };
        break;

      case 'popular':
        // Show most used templates
        filters = {
          sortBy: 'usageCount',
          sortOrder: 'desc',
          isActive: true,
        };
        break;

      case 'starred':
        // Show starred/favorited templates
        const starred = JSON.parse(localStorage.getItem('starredTemplates') || '[]');
        filters = {
          templateIds: starred,
        };
        break;

      default:
        break;
    }

    setQuickFilter(filterType);
    applyFilters(filters);
  };

  const applyFilters = (filters) => {
    form.setFieldsValue(filters);
    setActiveFilters(filters);
    onFilterChange?.(filters);
  };

  const handleFilterChange = (changedValues, allValues) => {
    // Clear quick filter when manual filters are applied
    if (quickFilter) {
      setQuickFilter(null);
    }

    // Remove empty values
    const cleanedFilters = Object.entries(allValues).reduce((acc, [key, value]) => {
      if (value !== undefined && value !== null && value !== '' &&
          !(Array.isArray(value) && value.length === 0)) {
        acc[key] = value;
      }
      return acc;
    }, {});

    setActiveFilters(cleanedFilters);
    onFilterChange?.(cleanedFilters);
  };

  const handleClearAll = () => {
    form.resetFields();
    setActiveFilters({});
    setQuickFilter(null);
    onFilterChange?.({});
  };

  const handleSavePreset = () => {
    if (!presetName.trim()) {
      message.error('Please enter a preset name');
      return;
    }

    const preset = {
      id: Date.now().toString(),
      name: presetName.trim(),
      filters: activeFilters,
      createdAt: new Date().toISOString(),
    };

    const updated = [...savedPresets, preset];
    setSavedPresets(updated);
    localStorage.setItem('templateFilterPresets', JSON.stringify(updated));

    message.success(`Preset "${presetName}" saved`);
    setPresetName('');
    setShowSavePreset(false);
  };

  const handleLoadPreset = (preset) => {
    applyFilters(preset.filters);
    message.success(`Loaded preset "${preset.name}"`);
  };

  const handleDeletePreset = (presetId) => {
    const updated = savedPresets.filter(p => p.id !== presetId);
    setSavedPresets(updated);
    localStorage.setItem('templateFilterPresets', JSON.stringify(updated));
    message.success('Preset deleted');
  };

  const getActiveFilterCount = () => {
    return Object.keys(activeFilters).length;
  };

  const presetMenuItems = savedPresets.map(preset => ({
    key: preset.id,
    label: (
      <PresetMenuItem>
        <span onClick={() => handleLoadPreset(preset)}>
          {preset.name}
        </span>
        <DeleteButton
          type="text"
          size="small"
          danger
          icon={<DeleteOutlined />}
          onClick={(e) => {
            e.stopPropagation();
            handleDeletePreset(preset.id);
          }}
        />
      </PresetMenuItem>
    ),
  }));

  const savePresetContent = (
    <SavePresetForm>
      <Input
        placeholder="Enter preset name"
        value={presetName}
        onChange={(e) => setPresetName(e.target.value)}
        onPressEnter={handleSavePreset}
        autoFocus
      />
      <Space style={{ marginTop: 8 }}>
        <Button size="small" onClick={() => setShowSavePreset(false)}>
          Cancel
        </Button>
        <Button
          size="small"
          type="primary"
          icon={<SaveOutlined />}
          onClick={handleSavePreset}
        >
          Save
        </Button>
      </Space>
    </SavePresetForm>
  );

  return (
    <Container>
      {/* Quick Filters */}
      <QuickFiltersRow>
        <QuickFilterButton
          icon={<UserOutlined />}
          onClick={() => handleQuickFilter('my-templates')}
          $active={quickFilter === 'my-templates'}
        >
          My Templates
        </QuickFilterButton>

        <QuickFilterButton
          icon={<ClockCircleOutlined />}
          onClick={() => handleQuickFilter('recent')}
          $active={quickFilter === 'recent'}
        >
          Recent
        </QuickFilterButton>

        <QuickFilterButton
          icon={<FireOutlined />}
          onClick={() => handleQuickFilter('popular')}
          $active={quickFilter === 'popular'}
        >
          Popular
        </QuickFilterButton>

        <QuickFilterButton
          icon={<StarOutlined />}
          onClick={() => handleQuickFilter('starred')}
          $active={quickFilter === 'starred'}
        >
          Starred
        </QuickFilterButton>
      </QuickFiltersRow>

      {/* Advanced Filters Card */}
      <FiltersCard>
        <CardHeader>
          <HeaderLeft>
            <FilterOutlined />
            <span>Advanced Filters</span>
            {getActiveFilterCount() > 0 && (
              <Badge count={getActiveFilterCount()} />
            )}
          </HeaderLeft>
          <HeaderRight>
            <Space>
              {savedPresets.length > 0 && (
                <Dropdown menu={{ items: presetMenuItems }} trigger={['click']}>
                  <Button size="small">
                    Load Preset <DownOutlined />
                  </Button>
                </Dropdown>
              )}

              <Popover
                content={savePresetContent}
                title="Save Filter Preset"
                trigger="click"
                open={showSavePreset}
                onOpenChange={setShowSavePreset}
              >
                <Tooltip title="Save current filters as preset">
                  <Button
                    size="small"
                    icon={<SaveOutlined />}
                    disabled={getActiveFilterCount() === 0}
                  >
                    Save Preset
                  </Button>
                </Tooltip>
              </Popover>

              <Button
                size="small"
                icon={<CloseCircleOutlined />}
                onClick={handleClearAll}
                disabled={getActiveFilterCount() === 0}
              >
                Clear All
              </Button>
            </Space>
          </HeaderRight>
        </CardHeader>

        <Form
          form={form}
          layout="vertical"
          onValuesChange={handleFilterChange}
        >
          <FiltersGrid>
            {/* Full-text Search */}
            <Form.Item
              label="Search All Fields"
              name="search"
              style={{ gridColumn: '1 / -1' }}
            >
              <Input
                prefix={<SearchOutlined />}
                placeholder="Search in name, description, category, HTML, prompts..."
                allowClear
              />
            </Form.Item>

            {/* Template Name */}
            <Form.Item label="Template Name" name="templateName">
              <Input placeholder="Filter by name" allowClear />
            </Form.Item>

            {/* Analysis Level */}
            <Form.Item label="Analysis Level" name="analysisLevel">
              <Select
                placeholder="Select level"
                allowClear
                mode="multiple"
              >
                <Select.Option value="page">Page Level</Select.Option>
                <Select.Option value="module">Module Level</Select.Option>
                <Select.Option value="dashboard">Dashboard Level</Select.Option>
                <Select.Option value="hrst">HRST Report</Select.Option>
              </Select>
            </Form.Item>

            {/* Scope */}
            <Form.Item label="Scope" name="scope">
              <Select
                placeholder="Select scope"
                allowClear
                mode="multiple"
              >
                <Select.Option value="SYSTEM">
                  <GlobalOutlined /> System
                </Select.Option>
                <Select.Option value="ORGANIZATION">
                  <TeamOutlined /> Organization
                </Select.Option>
                <Select.Option value="USER">
                  <UserOutlined /> User
                </Select.Option>
              </Select>
            </Form.Item>

            {/* Category */}
            <Form.Item label="Category" name="category">
              <Input placeholder="Filter by category" allowClear />
            </Form.Item>

            {/* Status */}
            <Form.Item label="Status" name="isActive">
              <Select placeholder="Select status" allowClear>
                <Select.Option value={true}>Active</Select.Option>
                <Select.Option value={false}>Inactive</Select.Option>
              </Select>
            </Form.Item>

            {/* Is Default */}
            <Form.Item label="Default Template" name="isDefault" valuePropName="checked">
              <Switch checkedChildren="Yes" unCheckedChildren="No" />
            </Form.Item>

            {/* Date Range */}
            <Form.Item label="Created Date Range" name="dateRange" style={{ gridColumn: '1 / -1' }}>
              <RangePicker style={{ width: '100%' }} />
            </Form.Item>

            {/* Sort By */}
            <Form.Item label="Sort By" name="sortBy">
              <Select placeholder="Select sort field" allowClear>
                <Select.Option value="createdAt">Created Date</Select.Option>
                <Select.Option value="updatedAt">Updated Date</Select.Option>
                <Select.Option value="templateName">Name</Select.Option>
                <Select.Option value="usageCount">Usage Count</Select.Option>
                <Select.Option value="version">Version</Select.Option>
              </Select>
            </Form.Item>

            {/* Sort Order */}
            <Form.Item label="Sort Order" name="sortOrder">
              <Select placeholder="Select order" allowClear>
                <Select.Option value="asc">Ascending</Select.Option>
                <Select.Option value="desc">Descending</Select.Option>
              </Select>
            </Form.Item>
          </FiltersGrid>
        </Form>

        {/* Active Filters Display */}
        {getActiveFilterCount() > 0 && (
          <>
            <Divider orientation="left" style={{ margin: '16px 0' }}>
              Active Filters ({getActiveFilterCount()})
            </Divider>
            <ActiveFiltersRow>
              {Object.entries(activeFilters).map(([key, value]) => {
                let displayValue = value;

                if (Array.isArray(value)) {
                  displayValue = value.join(', ');
                } else if (key === 'dateRange' && value) {
                  displayValue = `${value[0]?.format('MMM D')} - ${value[1]?.format('MMM D')}`;
                } else if (typeof value === 'boolean') {
                  displayValue = value ? 'Yes' : 'No';
                }

                return (
                  <Tag
                    key={key}
                    closable
                    onClose={() => {
                      form.setFieldValue(key, undefined);
                      handleFilterChange({}, form.getFieldsValue());
                    }}
                  >
                    <strong>{key}:</strong> {displayValue}
                  </Tag>
                );
              })}
            </ActiveFiltersRow>
          </>
        )}
      </FiltersCard>
    </Container>
  );
};

// Styled Components
const Container = styled.div`
  width: 100%;
`;

const QuickFiltersRow = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 16px;
  flex-wrap: wrap;
`;

const QuickFilterButton = styled(Button)`
  background: ${props => props.$active ? '#e6f7ff' : 'white'};
  border-color: ${props => props.$active ? '#1890ff' : '#d9d9d9'};
  color: ${props => props.$active ? '#1890ff' : '#595959'};
  font-weight: ${props => props.$active ? '600' : '400'};

  &:hover {
    background: #e6f7ff;
    border-color: #1890ff;
    color: #1890ff;
  }
`;

const FiltersCard = styled(Card)`
  .ant-card-body {
    padding: 20px;
  }
`;

const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  padding-bottom: 12px;
  border-bottom: 1px solid #f0f0f0;
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 600;
  font-size: 15px;

  .anticon {
    color: #1890ff;
    font-size: 16px;
  }
`;

const HeaderRight = styled.div``;

const FiltersGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 16px;
`;

const ActiveFiltersRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const PresetMenuItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 200px;

  span {
    flex: 1;
  }
`;

const DeleteButton = styled(Button)`
  opacity: 0;
  transition: opacity 0.2s;

  ${PresetMenuItem}:hover & {
    opacity: 1;
  }
`;

const SavePresetForm = styled.div`
  width: 250px;
`;

export default AdvancedFilters;
